import React, { useEffect, useState } from "react";
import { DepositContent } from "../../blocks/deposit-content/DepositContent";
import { MaxDeposit } from "../../blocks/max-deposit/MaxDeposit";
import * as styles from "./PayIdStep.module.scss";
import { Button, Modal, Skeleton, toast } from "@picklebet/ui";
import { PayIdCopyField } from "./PayIdCopyField";
import { SupportLink } from "features/shell/components/shared/SupportLink";
import { usePayID } from "hooks/firestore/user/usePayID";
import { walletService } from "features/wallet/services/wallet";
import { parseError } from "utilities/errorsParser";

const PAYID_SUPPORT_LABEL = "weblink-depositing-funds-using-payid";

const PayIdStepSkeleton = () => {
  return (
    <div className={styles.skeleton}>
      <div className={styles.descriptionSkeleton}>
        <Skeleton edge="hard" size="xs" />
        <Skeleton edge="hard" size="xs" />
        <Skeleton edge="hard" size="xs" span="half" />
      </div>

      <Skeleton className={styles.inputSkeleton} />
      <Skeleton className={styles.buttonSkeleton} />
      <Skeleton edge="hard" size="xs" />
    </div>
  );
};

/**
 * Hands over the details and stops: the deposit itself happens in the account
 * holder's banking app. No payment method field, so the wizard's back action
 * is the only way back to the picker.
 */
export const PayIdStep = () => {
  const { payId, isLoading: payIdLoading } = usePayID();

  const [generatingPayId, setGeneratingPayId] = useState(false);

  useEffect(() => {
    if (generatingPayId && payId) {
      setGeneratingPayId(false);
    }
  }, [generatingPayId, payId]);

  const generatePayId = async () => {
    setGeneratingPayId(true);

    const result = await walletService.generatePayID();

    if (!result.ok) {
      setGeneratingPayId(false);
      toast({
        variant: "danger",
        title: "Failed to create PayID",
        description: parseError(result.error),
      });
      return;
    }
  };

  // the deposit limit isn't waited on: MaxDeposit carries its own skeleton
  if (payIdLoading) {
    return <PayIdStepSkeleton />;
  }

  // a state rather than a step of its own: a PayID is issued once per account
  // and never changes, so there is nothing to go back to once it exists
  if (generatingPayId || !payId) {
    return (
      <DepositContent>
        <p className={styles.description}>
          We will generate a unique PayID email address linked to your Picklebet
          account, to allow you to deposit instantly.
        </p>

        <div className={styles.buttonContainer}>
          <Button
            loading={generatingPayId}
            variant={`primary`}
            onClick={generatePayId}
          >
            Start using PayID
          </Button>
        </div>

        <Modal.Footer>
          <SupportLink hideChevron options={{ labels: [PAYID_SUPPORT_LABEL] }}>
            Learn More
          </SupportLink>
        </Modal.Footer>
      </DepositContent>
    );
  }

  return (
    <DepositContent>
      <p className={styles.description}>
        When depositing using your online banking app, select &quot;Email&quot;
        as the PayID type, and paste your unique email.
      </p>

      <PayIdCopyField value={payId} />

      <MaxDeposit supportLabel={PAYID_SUPPORT_LABEL} />
    </DepositContent>
  );
};
